import { get, post, API_URL } from '../utils/api';

// Configure API URL from environment or use default
// const API_URL = import.meta.env.VITE_API_URL || '/api';

// Track failed requests to avoid overloading server with retries
const failedRequests = {};

// The payment backend (Yoco) denominates amounts in cents. Convert to rands at
// the boundary so every consumer works in a single, consistent unit.
const centsToRands = (amountInCents) =>
  typeof amountInCents === 'number' ? amountInCents / 100 : 0;

/**
 * Reset failed requests tracking
 * @param {string} endpoint - Optional specific endpoint to reset, if not provided all endpoints are reset
 */
export const resetFailedRequests = (endpoint = null) => {
  if (endpoint) {
    delete failedRequests[endpoint];
    // console.log(`Reset failure tracking for endpoint: ${endpoint}`);
  } else {
    // Reset all failed requests
    const count = Object.keys(failedRequests).length;
    Object.keys(failedRequests).forEach(key => {
      delete failedRequests[key];
    });
    // console.log(`Reset failure tracking for ${count} endpoints`);
  }
};

// Helper to track failed endpoints and determine if we should try again
const shouldRetryRequest = (endpoint) => {
  if (!failedRequests[endpoint]) {
    failedRequests[endpoint] = {
      count: 1,
      lastAttempt: Date.now()
    };
    return true; // First failure, try again
  }
  
  const lastAttempt = failedRequests[endpoint].lastAttempt;
  const now = Date.now();
  const timeDiff = (now - lastAttempt) / 1000; // seconds
  
  let retryDelay;
  
  if (failedRequests[endpoint].count <= 1) {
    retryDelay = 5; // 5 seconds
  } else if (failedRequests[endpoint].count <= 2) {
    retryDelay = 30; // 30 seconds
  } else if (failedRequests[endpoint].count <= 3) {
    retryDelay = 120; // 2 minutes
  } else {
    retryDelay = 300; // 5 minutes
  }
  
  if (timeDiff > retryDelay) {
    // Update last attempt time
    failedRequests[endpoint].lastAttempt = now;
    failedRequests[endpoint].count += 1;
    return true; // Try again after delay
  }
  
  return false; // Don't try again yet
};

/**
 * Fetch payment history for a specific account
 * Calls our auth-server API which proxies to the payment backend
 * 
 * @param {string} accountUuid - UUID of the account to fetch payments for
 * @returns {Promise<Array>} - Promise that resolves to an array of payment objects
 */
export const getPaymentsByAccount = async (accountUuid) => {
  if (!accountUuid) {
    // console.error('Missing accountUuid parameter for getPaymentsByAccount');
    return [];
  }

  // Convert account UUID to uppercase for the API
  const formattedUuid = accountUuid.toUpperCase();
  const endpoint = `${API_URL}/payment/payments/byAccount/${formattedUuid}`;
  // console.log(`Fetching payments for account: ${formattedUuid}`);
  
  // Check if we should try this endpoint
  if (failedRequests[endpoint] && !shouldRetryRequest(endpoint)) {
    // console.log(`Skipping request to ${endpoint} due to recent failures`);
    return [];
  }
  
  try {
    const response = await get(endpoint);
    
    // If successful, reset failure count
    if (failedRequests[endpoint]) {
      delete failedRequests[endpoint];
    }
    
    return formatPaymentsForTransactionCard(response.data);
  } catch (error) {
    // console.error('Error fetching payment history:', error);
    
    // Track failure
    if (!failedRequests[endpoint]) {
      failedRequests[endpoint] = { count: 0, lastAttempt: 0 };
    }
    shouldRetryRequest(endpoint);
    
    return [];
  }
};

/**
 * Fetch payment details by payment ID
 * Calls our auth-server API which proxies to the payment backend
 * 
 * @param {string} paymentId - ID of the payment to fetch
 * @returns {Promise<Object>} - Promise that resolves to a payment object
 */
export const getPaymentById = async (paymentId) => {
  if (!paymentId) {
    // console.error('Missing paymentId parameter for getPaymentById');
    return null;
  }

  const endpoint = `${API_URL}/payment/payments`;
  
  // Check if we should try this endpoint
  if (failedRequests[endpoint] && !shouldRetryRequest(endpoint)) {
    // console.log(`Skipping request to ${endpoint} due to recent failures`);
    return null;
  }
  
  try {
    const response = await get(endpoint, { id: paymentId });
    
    // If successful, reset failure count
    if (failedRequests[endpoint]) {
      delete failedRequests[endpoint];
    }
    
    return response.data;
  } catch (error) {
    // console.error('Error fetching payment details:', error);
    
    // Track failure
    if (!failedRequests[endpoint]) {
      failedRequests[endpoint] = { count: 0, lastAttempt: 0 };
    }
    shouldRetryRequest(endpoint);
    
    return null;
  }
};

/**
 * Format payment data for display in the UI
 * This function converts raw payment data from the API to the format expected by the RecentPrepaidTransactionsCard
 * 
 * @param {Array} payments - Array of payment objects from the API
 * @returns {Array} - Array of formatted transaction objects
 */
export const formatPaymentsForTransactionCard = (payments = []) => {
  if (!payments || !Array.isArray(payments)) return [];
  
  return payments.map(payment => ({
    id: payment.uuid || payment.paymentId,
    date: payment.createdAt || payment.date,
    type: payment.paymentMethodType || payment.cardScheme || 'Purchase',
    amount: centsToRands(payment.amount),
    status: payment.status || 'Unknown'
  }));
};

/**
 * Get payment transactions by account UUID
 * @param {string} accountUuid - Account UUID
 * @returns {Promise<Array>} - List of transactions
 */
export const getTransactionsByAccount = async (accountUuid) => {
  if (!accountUuid) {
    // console.error('Missing accountUuid parameter for getTransactionsByAccount');
    return [];
  }

  try {
    const response = await get(`${API_URL}/payment/payments/byAccount/${accountUuid}`);
    const transactions = Array.isArray(response.data) ? response.data : [];
    // Normalise amounts to rands so this path matches formatPaymentsForTransactionCard.
    return transactions.map((transaction) => ({
      ...transaction,
      amount: centsToRands(transaction.amount),
    }));
  } catch (error) {
    // console.error('Error fetching payment transactions:', error);
    return [];
  }
};

/**
 * Get a specific transaction by ID
 * @param {string} transactionId - Transaction ID
 * @returns {Promise<Object>} - Transaction details
 */
export const getTransactionById = async (transactionId) => {
  if (!transactionId) {
    // console.error('Missing transactionId parameter for getTransactionById');
    return null;
  }

  try {
    const response = await get(`${API_URL}/payment/payments/${transactionId}`);
    return response.data || null;
  } catch (error) {
    // console.error('Error fetching transaction details:', error);
    return null;
  }
};

/**
 * Clear the transaction cache for a specific account
 * @param {string} accountUuid - Account UUID
 */
export const clearTransactionCache = (accountUuid) => {
  if (!accountUuid) return;
  
  try {
    // Remove specific account cache
    sessionStorage.removeItem(`prepaid_transactions_${accountUuid}`);
    
    // Also remove the stale flag if it exists
    sessionStorage.removeItem('transaction_cache_stale');
    
    // console.log(`Cleared transaction cache for account: ${accountUuid}`);
  } catch (e) {
    // console.error('Error clearing transaction cache:', e);
  }
};

/**
 * Mark the transaction cache as stale
 * This should be called whenever a new transaction is initiated
 */
export const markTransactionCacheStale = () => {
  try {
    sessionStorage.setItem('transaction_cache_stale', 'true');
    // console.log('Marked transaction cache as stale');
  } catch (e) {
    // console.error('Error marking transaction cache as stale:', e);
  }
};

// User-facing messages for checkout creation failures, keyed by HTTP status
// (backend contract section 8).
const CHECKOUT_ERROR_MESSAGES = {
  400: 'Please check your payment details and try again.',
  409: 'This payment was already submitted.',
  502: 'The payment service is temporarily unavailable. Please try again.',
};

const GENERIC_CHECKOUT_ERROR = 'Something went wrong starting your payment. Please try again.';

// The Java backend answers errors in plain text, but the Node proxy rewraps
// them as {status:'error', message}. Accept both shapes.
const extractCheckoutErrorReason = (data) => {
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (data && typeof data.message === 'string') return data.message;
  return '';
};

/**
 * Create a provider-neutral hosted checkout. The backend decides per site
 * whether Yoco or Peach handles it; the caller only needs redirectUrl.
 *
 * @param {Object} params
 * @param {string} params.siteUuid - Site being paid for
 * @param {string} params.accountUuid - Account to credit
 * @param {string} params.unitUuid - Unit UUID (not the numeric unitId)
 * @param {string} params.userUuid - Paying user
 * @param {number} params.amountCents - Integer cents, > 0
 * @returns {Promise<{checkoutUuid: string, redirectUrl: string, provider: string, status: string}>}
 */
export const createCheckout = async ({ siteUuid, accountUuid, unitUuid, userUuid, amountCents }) => {
  let response;
  try {
    response = await post(`${API_URL}/java/cxf/uns/payment/checkouts`, {
      siteUuid,
      accountUuid,
      unitUuid,
      userUuid,
      amountCents,
      currency: 'ZAR',
      idempotencyKey: crypto.randomUUID(),
    });
  } catch (error) {
    const status = error.response?.status;
    const checkoutError = new Error(CHECKOUT_ERROR_MESSAGES[status] || GENERIC_CHECKOUT_ERROR, { cause: error });
    checkoutError.status = status;
    checkoutError.reason = extractCheckoutErrorReason(error.response?.data);
    throw checkoutError;
  }

  // A 2xx through the proxy is not proof of a well-formed checkout - guard
  // the boundary so downstream code never sees a partial object.
  const checkout = response.data;
  if (!checkout?.checkoutUuid || !checkout?.redirectUrl) {
    const invalidError = new Error(GENERIC_CHECKOUT_ERROR);
    invalidError.reason = 'Malformed checkout response from backend';
    throw invalidError;
  }
  return checkout;
};
